import { Box } from '@chakra-ui/react';
import { type ReactNode, useEffect, useRef } from 'react';

type GridGlowBackgroundProps = {
  children: ReactNode;
  glowColors?: string[];
  glowCount?: number;
};

/** Соотношение сторон пятна: чуть шире, чем выше — круглые читаются как «шарики». */
const GLOW_STRETCH = 1.35;

/**
 * `#rgb` / `#rrggbb` → `rgba(...)`. Цвет приходит из настроек игры, поэтому нехекс (уже готовый
 * `rgb()`, имя цвета) возвращаем как есть: пусть лучше пятно будет плотнее задуманного, чем
 * пропадёт совсем.
 */
const withAlpha = (color: string, alpha: number) => {
  const hex = color.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return color;
  const full =
    hex.length === 4
      ? `${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex.slice(1);
  const int = Number.parseInt(full, 16);
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

/**
 * Мягкие цветные пятна, медленно переползающие за контентом.
 *
 * Раньше здесь поверх пятен рисовалась ещё и белая сетка 50×50, а сами пятна ходили строго по её
 * узлам. Сетку убрали: она ничего не выравнивала (у контента своя колонка на 1200px) и лежала
 * ровно тем «тёмный фон + техносетка + свечение», по которому лендинг и опознают как шаблонный.
 * Вместе с ней ушло и округление цели до узла — без сетки дискретное движение выглядело
 * сломанным, а не ритмичным.
 *
 * Цвет приходит снаружи: на странице игры это её акцент, поэтому фон под оранжевым героем Apex
 * больше не фиолетовый.
 */
export const GridGlowBackground = ({
  children,
  glowColors = ['#4B50E6', '#8E7BFF', '#4B50E6'],
  glowCount = 5,
}: GridGlowBackgroundProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Массив цветов почти всегда приходит литералом прямо в JSX: по ссылке эффект пересобирался
  // бы на каждый рендер родителя, заново создавая все пятна.
  const colorsKey = glowColors.join(',');

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const context = el.getContext('2d');
    if (!context) return;
    // Локальные константы: внутри класса сужение типа по раннему возврату уже не действует.
    const canvas = el;
    const ctx = context;

    const colors = colorsKey.split(',');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let glows: Glow[] = [];
    let frameId: number | null = null;

    class Glow {
      x: number;
      y: number;
      targetX: number;
      targetY: number;
      radius: number;
      speed: number;
      color: string;
      alpha: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.targetX = this.x;
        this.targetY = this.y;
        // Пятна крупные и разноразмерные: мелкие на широком экране читались как точки.
        this.radius = Math.random() * 170 + 150;
        this.speed = Math.random() * 0.0016 + 0.0009;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.alpha = 0;
        this.setNewTarget();
      }

      setNewTarget() {
        this.targetX = Math.random() * canvas.width;
        this.targetY = Math.random() * canvas.height;
      }

      update() {
        this.x += (this.targetX - this.x) * this.speed;
        this.y += (this.targetY - this.y) * this.speed;

        // Порог кратен размеру пятна: у медленной интерполяции последние пиксели тянутся
        // дольше, чем весь путь, и на фиксированной единице цель почти не менялась.
        if (Math.hypot(this.targetX - this.x, this.targetY - this.y) < this.radius * 0.12) {
          this.setNewTarget();
        }
        if (this.alpha < 1) this.alpha += 0.008;
      }

      draw() {
        ctx.globalAlpha = this.alpha;
        // Затухание идёт круто и рано: у двух стопов «цвет → прозрачный» пятно держит почти
        // полную силу до середины радиуса, и пять таких пятен сливались в сплошную заливку
        // вместо подсветки на тёмном фоне.
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius);
        grad.addColorStop(0, withAlpha(this.color, 0.21));
        grad.addColorStop(0.35, withAlpha(this.color, 0.06));
        grad.addColorStop(0.7, withAlpha(this.color, 0.012));
        grad.addColorStop(1, withAlpha(this.color, 0));
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.scale(GLOW_STRETCH, 1);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();
        ctx.globalAlpha = 1;
      }
    }

    const paint = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const g of glows) {
        g.draw();
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
      glows = Array.from({ length: glowCount }, () => new Glow());
      // Со вкладки, свёрнутой при загрузке, кадр не придёт вовсе — рисуем первый сами.
      if (reduceMotion) {
        for (const g of glows) {
          g.alpha = 1;
        }
      }
      paint();
    };

    const animate = () => {
      for (const g of glows) {
        g.update();
      }
      paint();
      frameId = requestAnimationFrame(animate);
    };

    const start = () => {
      if (frameId === null) frameId = requestAnimationFrame(animate);
    };

    const stop = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    };

    resize();
    window.addEventListener('resize', resize);

    // Кадры считаются только пока фон на экране. Компонент стоит в первом экране четырёх страниц,
    // и раньше он крутил requestAnimationFrame всё время, пока страница открыта.
    const observer = new IntersectionObserver(([entry]) => (entry?.isIntersecting ? start() : stop()));
    observer.observe(canvas);

    if (reduceMotion) {
      stop();
      observer.disconnect();
    }

    return () => {
      window.removeEventListener('resize', resize);
      observer.disconnect();
      stop();
    };
  }, [colorsKey, glowCount]);

  return (
    <Box position="relative" w="100%" overflow="hidden">
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          width: '100%',
          height: '100%',
          opacity: 0.85,
        }}
      />
      <Box position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  );
};
